import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { offersAPI } from '../../services/api';
import Header from '../../components/layout/Header';
import { CUISINE_TYPES, UDAIPUR_AREAS } from '../../types';

export default function OffersPage() {
  const [filters, setFilters] = useState<any>({});

  const { data, isLoading } = useQuery({
    queryKey: ['offers', filters],
    queryFn: () => offersAPI.getAll(filters).then((res) => res.data),
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">All Offers</h1>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Filters Sidebar */}
          <aside className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow p-6 sticky top-24">
              <h2 className="font-semibold text-gray-900 mb-4">Filters</h2>

              {/* Cuisine Filter */}
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Cuisine</h3>
                <select
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  value={filters.cuisine || ''}
                  onChange={(e) => setFilters({ ...filters, cuisine: e.target.value || undefined })}
                >
                  <option value="">All Cuisines</option>
                  {CUISINE_TYPES.map((cuisine) => (
                    <option key={cuisine} value={cuisine}>
                      {cuisine}
                    </option>
                  ))}
                </select>
              </div>

              {/* Area Filter */}
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Area</h3>
                <select
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  value={filters.area || ''}
                  onChange={(e) => setFilters({ ...filters, area: e.target.value || undefined })}
                >
                  <option value="">All Areas</option>
                  {UDAIPUR_AREAS.map((area) => (
                    <option key={area} value={area}>
                      {area}
                    </option>
                  ))}
                </select>
              </div>

              {/* Discount Filter */}
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Min Discount</h3>
                <input
                  type="number"
                  placeholder="e.g., 20"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  value={filters.minDiscount || ''}
                  onChange={(e) =>
                    setFilters({ ...filters, minDiscount: e.target.value ? parseInt(e.target.value) : undefined })
                  }
                />
              </div>

              <button
                onClick={() => setFilters({})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Reset Filters
              </button>
            </div>
          </aside>

          {/* Offers Grid */}
          <main className="lg:col-span-3">
            {isLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="bg-white rounded-lg shadow h-96 animate-pulse" />
                ))}
              </div>
            ) : data?.data && data.data.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {data.data.map((item: any) => (
                  <Link
                    key={item.offer.id}
                    to={`/offers/${item.offer.id}`}
                    className="bg-white rounded-lg shadow overflow-hidden hover:shadow-lg transition"
                  >
                    <div className="relative h-48 bg-gray-200">
                      {item.offer.imageUrl ? (
                        <img
                          src={item.offer.imageUrl}
                          alt={item.offer.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-400">
                          No Image
                        </div>
                      )}
                      {item.offer.discountPercentage && (
                        <div className="absolute top-4 right-4 bg-red-600 text-white px-3 py-1 rounded-full font-bold text-sm">
                          {item.offer.discountPercentage}% OFF
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <p className="text-sm text-gray-600 mb-1">{item.restaurant?.name}</p>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        {item.offer.title}
                      </h3>
                      <div className="flex items-center gap-2 mb-2">
                        {item.offer.offerPrice && (
                          <span className="text-xl font-bold text-green-600">
                            ₹{item.offer.offerPrice}
                          </span>
                        )}
                        {item.offer.originalPrice && (
                          <span className="text-gray-400 line-through">
                            ₹{item.offer.originalPrice}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500">
                        Valid until {new Date(item.offer.validTo).toLocaleDateString()}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-600">No offers found matching your criteria.</p>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
